import * as XLSX from "xlsx";
import {saveAs} from "file-saver";
import {apiGet, apiPost} from "../../shared/api/client";
import i18n from "../../shared/i18n";
import {asList} from "../../shared/utils/format";

export const DEFAULT_PAY_REPORT_PAGE_SIZE = 20;
export const PAY_REPORT_DETAIL_PAGE_SIZE = 100;

const cleanParams = (filters) =>
    Object.fromEntries(Object.entries(filters || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined));

const withListPageSize = (filters = {}) => {
    const params = cleanParams(filters);
    if (Number(params.page_size) === DEFAULT_PAY_REPORT_PAGE_SIZE) {
        delete params.page_size;
    }
    return params;
};

/** Turn absolute/legacy download URLs into a path apiClient can call with Token auth. */
export const payReportDownloadPath = (downloadUrl) => {
    if (!downloadUrl) return null;
    try {
        const url = new URL(downloadUrl, "https://admin.invalid");
        let pathname = url.pathname;
        if (pathname.includes("/pay/place/report/download")) {
            pathname = "/api/v2/admin/pay/report/download";
        }
        return `${pathname}${url.search}`;
    } catch (_error) {
        return null;
    }
};

export const loadPayReport = async (filters = {}) => {
    const params = withListPageSize(filters);
    return apiGet("/api/v2/admin/pay/report", {params});
};

export const loadPayPlaceGroups = async (filters = {}) => {
    const params = withListPageSize(filters);
    return apiGet("/api/v2/admin/pay/report/places", {params});
};

export const loadAllPayReportRows = async (filters = {}) => {
    const first = await loadPayReport({...filters, page: 1, page_size: PAY_REPORT_DETAIL_PAGE_SIZE});
    const rows = [...asList(first)];
    const total = typeof first?.count === "number" ? first.count : rows.length;
    let page = 2;
    while (rows.length < total) {
        const more = await loadPayReport({...filters, page, page_size: PAY_REPORT_DETAIL_PAGE_SIZE});
        const nextRows = asList(more);
        if (!nextRows.length) {
            break;
        }
        rows.push(...nextRows);
        page += 1;
    }
    return rows;
};

export const loadTopupReport = async (filters = {}) => {
    const params = withListPageSize(filters);
    return apiGet("/api/v2/admin/topups/report", {params});
};

export const loadClubReport = async (filters = {}) => {
    const params = withListPageSize(filters);
    return apiGet("/api/v2/admin/club/report", {params});
};

export const buildPayReportDownloadUrl = (filters = {}) => {
    const params = new URLSearchParams(withListPageSize({
        date_from: filters.date_from,
        date_to: filters.date_to,
        place: filters.place,
        branch: filters.branch,
    }));
    const query = params.toString();
    return `/api/v2/admin/pay/report/download${query ? `?${query}` : ""}`;
};

export const searchPayNames = async ({place_name = "", branch_name = ""}) =>
    apiPost("/api/v2/admin/pay/search-names", {place_name, branch_name});

/** Authenticated JSON→xlsx export for place payment reports (ENG-179). */
export const exportPayReportSpreadsheet = async (downloadUrl, filename = "pay-report") => {
    const path = payReportDownloadPath(downloadUrl);
    if (!path) {
        throw new Error(i18n.t("money.finance.export.invalidLink"));
    }
    const rows = await apiGet(path);
    const worksheet = XLSX.utils.json_to_sheet(Array.isArray(rows) ? rows : []);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, i18n.t("money.finance.export.sheet"));
    const excelBuffer = XLSX.write(workbook, {bookType: "xlsx", type: "array"});
    saveAs(new Blob([excelBuffer], {type: "application/octet-stream"}), `${filename}.xlsx`);
};
